import type { SupabaseClient } from '@supabase/supabase-js';
import { DUPLICATE_NAME, LONG_NAME, MAX_PROJECTS, type CreatorProject } from '../config/creator';
import { accountClient } from './accountUi';

/**
 * Where Level Creator projects live (spec 2026-11-01). Two separate sets:
 * signed in → the account's `creator_levels` rows (written only through the
 * validating `save_creator_level` RPC); signed out → this device's
 * localStorage. The menu shows one set at a time and logout never touches the
 * device set. A project is only ever read back by its author.
 */
export interface CreatorStore {
  list(): Promise<CreatorProject[]>;
  /** Saves (insert or update) and returns the project as stored. */
  save(p: CreatorProject): Promise<CreatorProject>;
  remove(id: string): Promise<void>;
}

export const CREATOR_STORAGE_KEY = 'hambi.creator';
export const TOO_MANY_PROJECTS = `You can keep ${MAX_PROJECTS} levels — delete one first`;
export const SAVE_FAILED = "Couldn't save your level — try again";

type KeyValue = Pick<Storage, 'getItem' | 'setItem'>;

function readAll(storage: KeyValue): CreatorProject[] {
  try {
    const raw = storage.getItem(CREATOR_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as CreatorProject[]) : [];
  } catch {
    return [];
  }
}

const newestFirst = (a: CreatorProject, b: CreatorProject): number => b.updatedAt - a.updatedAt;

/** The signed-out set, on this device. */
export function localCreatorStore(storage: KeyValue, now: () => number = Date.now): CreatorStore {
  return {
    async list() {
      return readAll(storage).sort(newestFirst);
    },
    async save(p) {
      const all = readAll(storage);
      const i = all.findIndex((x) => x.id === p.id);
      if (i < 0 && all.length >= MAX_PROJECTS) throw new Error(TOO_MANY_PROJECTS);
      const saved = { ...p, updatedAt: now() };
      if (i < 0) all.push(saved);
      else all[i] = saved;
      storage.setItem(CREATOR_STORAGE_KEY, JSON.stringify(all));
      return saved;
    },
    async remove(id) {
      storage.setItem(
        CREATOR_STORAGE_KEY,
        JSON.stringify(readAll(storage).filter((x) => x.id !== id)),
      );
    },
  };
}

/**
 * The RPC arguments: the name and the description travel on their own, never
 * inside `def` (which the server refuses if it carries any prose).
 */
export function projectToRow(p: CreatorProject): {
  p_id: string;
  p_name: string;
  p_def: Omit<CreatorProject, 'id' | 'name' | 'updatedAt' | 'description'>;
  p_description: string | null;
} {
  const { id, name, updatedAt: _u, description, ...def } = p;
  return { p_id: id, p_name: name, p_def: def, p_description: description || null };
}

export function rowToProject(row: {
  id: string;
  name: string;
  def: unknown;
  updated_at: string;
  description?: string | null;
}): CreatorProject {
  const def = row.def as Omit<CreatorProject, 'id' | 'name' | 'updatedAt'>;
  return {
    ...def,
    id: row.id,
    name: row.name,
    ...(row.description ? { description: row.description } : {}),
    updatedAt: Date.parse(row.updated_at) || 0,
  };
}

export const LEVEL_TOO_BIG =
  "Couldn't save — a wave has too many enemy types, or the level is too big";
export const LOAD_FAILED = "Couldn't load your levels — check your connection and try again";
export const LEVEL_LOCKED = 'This level is locked while an admin reviews your unban request';
export const DELETE_FAILED = "Couldn't delete that level — try again";

/** Maps the RPC's refusals onto the copy the screens show. */
export function saveErrorMessage(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('creator_levels_name') || m.includes('duplicate')) return DUPLICATE_NAME;
  if (m.includes('too many')) return TOO_MANY_PROJECTS;
  if (m.includes('bad level')) return LEVEL_TOO_BIG;
  if (m.includes('bad name')) return LONG_NAME;
  if (m.includes('level locked')) return LEVEL_LOCKED;
  return SAVE_FAILED;
}

/** The signed-in set, on the account. */
export function remoteCreatorStore(client: SupabaseClient): CreatorStore {
  return {
    async list() {
      const { data, error } = await client
        .from('creator_levels')
        .select('id, name, def, description, updated_at')
        .order('updated_at', { ascending: false });
      // A failed read is NOT "no levels" — the menu would tell the player
      // their levels were gone, and the settings would make a duplicate.
      if (error || !data) throw new Error(LOAD_FAILED);
      return data.map(rowToProject);
    },
    async save(p) {
      const { data, error } = await client.rpc('save_creator_level', projectToRow(p));
      if (error) throw new Error(saveErrorMessage(error.message));
      // The server may have redacted the name (emails / phone numbers / links).
      const { data: row } = await client
        .from('creator_levels')
        .select('id, name, def, description, updated_at')
        .eq('id', (data as string) ?? p.id)
        .maybeSingle();
      return row ? rowToProject(row) : p;
    },
    async remove(id) {
      const { error } = await client.from('creator_levels').delete().eq('id', id);
      if (error) throw new Error(DELETE_FAILED);
    },
  };
}

/**
 * The set for whoever is playing right now. Decided from the auth SESSION,
 * not the loaded profile: right after a reload the profile is still loading,
 * and a signed-in player's level must not land in the device set (which a
 * later signed-out player on the same machine would see). Scenes pass
 * `saveStorage()`, so the ?debug page's sandbox keeps its device levels.
 */
export async function resolveCreatorStore(
  local: KeyValue,
  client: SupabaseClient | null = accountClient(),
): Promise<{ kind: 'account' | 'device'; store: CreatorStore }> {
  if (client) {
    try {
      const { data } = await client.auth.getSession();
      if (data.session) return { kind: 'account', store: remoteCreatorStore(client) };
    } catch {
      /* no session readable: device */
    }
  }
  return { kind: 'device', store: localCreatorStore(local) };
}

/** A fresh project id (a uuid, so the same id works on the account). */
export function newProjectId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback: RFC4122-shaped from Math.random (render layer, not the sim).
  return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () =>
    Math.floor(Math.random() * 16).toString(16),
  );
}
